import { InputLabel } from '@mui/material';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Gender } from 'config/enums';
import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import {
  callIdSelector,
  callLanguageCodeSelector,
} from 'features/call/call-base/store/selectors';
import { ColdHandoffStatus } from 'features/cold-handoff/enums';
import { ColdHandoffDaoService } from 'features/cold-handoff/services';
import { setColdHandoffStatus } from 'features/cold-handoff/store';
import { HandoffRequestContainer } from 'features/collaboration-base/components';
import { usePopoverContext } from 'features/menu/context';
import { handleError } from 'features/notification/store';
import styled from 'features/theme/styled';
import theme from 'features/theme/theme';
import { SelectGender, SelectLanguage } from 'features/ui/components';
import { Button } from 'features/ui/elements';
import {
  isVoiceSessionActiveSelector,
  voiceSessionIdSelector,
} from 'features/voice-session/store';

export const ColdHandoffRequest: FC = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [gender, setGender] = useState(Gender.U);
  const dispatch = useAppDispatch();
  const callId = useAppSelector(callIdSelector);
  const language = useAppSelector(callLanguageCodeSelector);
  const [selectedLanguage, setSelectedLanguage] = useState(language);
  const voiceSessionId = useAppSelector(voiceSessionIdSelector);
  const isVoiceSessionActive = useAppSelector(isVoiceSessionActiveSelector);
  const { handleClosePopover } = usePopoverContext();

  const handleRequestHandoff = async () => {
    setIsLoading(true);
    try {
      await ColdHandoffDaoService.requestHandoff({
        callId,
        language: selectedLanguage,
        gender: String(gender),
        voiceSessionId: isVoiceSessionActive ? voiceSessionId : undefined,
      });
      dispatch(setColdHandoffStatus(ColdHandoffStatus.REQUESTED));
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'handleRequestHandoff',
        })
      );
    } finally {
      setIsLoading(false);
      handleClosePopover();
    }
  };

  return (
    <HandoffRequestContainer testId='cold-handoff-request'>
      <Label>{t('genderPreferenceQuestion')}</Label>
      <SelectGender
        value={gender}
        onChange={(event) => setGender(event.target.value as Gender)}
      />
      <LanguageLabel>{t('teamRequest.requestLanguage')}</LanguageLabel>
      <SelectLanguage
        value={selectedLanguage}
        onChange={(e) => setSelectedLanguage(e.target.value)}
      />
      <SubmitButton
        type='submit'
        color='primary'
        size='small'
        fullWidth
        title={t('handoff')}
        onClick={handleRequestHandoff}
        loading={isLoading}
      >
        {t('handoff')}
      </SubmitButton>
      <CancelButton
        color='secondary'
        size='small'
        fullWidth
        title={t('cancel')}
        onClick={handleClosePopover}
      >
        {t('cancel')}
      </CancelButton>
    </HandoffRequestContainer>
  );
};

const Label = styled(InputLabel)`
  margin-bottom: ${theme.spacing(3)};
`;

const SubmitButton = styled(Button)`
  margin-top: ${theme.spacing(8)};
`;

const CancelButton = styled(Button)`
  margin-top: ${theme.spacing(4)};
`;

const LanguageLabel = styled(Label)`
  margin-top: ${theme.spacing(8)};
`;
